import { useDivisions } from "./useDivisions"
import { useUIStore } from "../stores"

type ModuleName = "bk" | "pi" | "psf" // Add other modules as needed

// Map short module names to database values
const MODULE_NAME_MAP: Record<ModuleName, string> = {
  bk: "bankruptcy",
  pi: "pi",
  psf: "psf",
}

/**
 * Check if a specific module is enabled for the selected division.
 * When "All Divisions" is selected (null), returns true if ANY division has the module.
 *
 * @param moduleName - The module name to check (e.g., "bk" for Bankruptcy)
 * @returns boolean - Whether the module is enabled
 */
export function useModuleEnabled(moduleName: ModuleName): boolean {
  const { data: divisions = [] } = useDivisions()
  const selectedDivision = useUIStore((state) => state.selectedDivision)
  const dbModuleName = MODULE_NAME_MAP[moduleName]

  if (!divisions.length) return false

  if (selectedDivision) {
    // Specific division selected - check if that division has the module
    const division = divisions.find((d) => d._id === selectedDivision)
    return division?.modules?.includes(dbModuleName) ?? false
  }

  // "All Divisions" selected - show if ANY division has the module
  return divisions.some((d) => d.modules?.includes(dbModuleName))
}

/**
 * Get divisions that have a specific module enabled.
 *
 * @param moduleName - The module name to filter by
 * @returns Array of divisions with the module enabled
 */
export function useModuleDivisions(moduleName: ModuleName) {
  const { data: divisions = [] } = useDivisions()
  const dbModuleName = MODULE_NAME_MAP[moduleName]
  return divisions.filter((d) => d.modules?.includes(dbModuleName))
}
